/**
 * Hook: useSpeechStreaming
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R07, SPEECH-V1-R08
 *
 * React hook for the real-time streaming speech-to-text path. Wraps
 * SpeechStreamingSession, exposing the live interim transcript (display-only,
 * SPEECH-V1-R07/R08) and surfacing final segments to the caller.
 *
 * SPEECH-V1-R08: this hook never submits anything to chat/search. It only
 * surfaces text; the consuming component decides when to submit the final
 * transcript (on the user's explicit send), exactly as for typed input.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { SpeechStreamingSession } from '@/services/speech-streaming';

export interface UseSpeechStreamingOptions {
  /** Optional BCP-47 language hint. */
  language?: string;
  /** Called for each final segment (text + confidence). */
  onFinal?: (text: string, confidence: number) => void;
  /** Called on error. */
  onError?: (message: string) => void;
}

export interface UseSpeechStreamingReturn {
  /** Whether a streaming session is active. */
  isStreaming: boolean;
  /** Current live (non-final) hypothesis for display. */
  interimText: string;
  /** Error message, if any. */
  error: string | null;
  /** Start a streaming session (requests the microphone). */
  start: () => Promise<void>;
  /** Stop the session and finalize. */
  stop: () => void;
}

export function useSpeechStreaming(
  options: UseSpeechStreamingOptions = {}
): UseSpeechStreamingReturn {
  const { language, onFinal, onError } = options;

  const sessionRef = useRef<SpeechStreamingSession | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    // Ask the session to finalize, but keep the reference and the on-screen
    // interim text: the backend still has to drain Azure's last result and
    // deliver it as a `final`, which is the only output that reaches the
    // input box. Clearing here made a just-spoken phrase disappear on stop.
    //
    // onFinal clears interimText when the real text arrives, and onClosed
    // clears the rest once the socket actually closes, so both happen in
    // response to the server rather than ahead of it.
    sessionRef.current?.stop();
    setIsStreaming(false);
  }, []);

  const start = useCallback(async () => {
    // A session that is still draining its final segment is no longer
    // capturing, so a new one may start over the top of it. Only an
    // actively-capturing session blocks a restart.
    if (sessionRef.current?.isActive()) {
      return;
    }
    // Drop any still-draining session; its onClosed must not clear state
    // belonging to the new one.
    sessionRef.current?.detach();
    setError(null);
    setInterimText('');
    const session = new SpeechStreamingSession();
    sessionRef.current = session;

    await session.start(
      {
        onReady: () => setIsStreaming(true),
        onInterim: (text) => setInterimText(text),
        onFinal: (text, confidence) => {
          setInterimText('');
          onFinal?.(text, confidence);
        },
        onError: (message) => {
          setError(message);
          onError?.(message);
          setIsStreaming(false);
        },
        onClosed: () => {
          setIsStreaming(false);
          setInterimText('');
          sessionRef.current = null;
        },
      },
      { language }
    );
  }, [language, onFinal, onError]);

  // Tear down on unmount. detach() rather than stop(): there is no component
  // left to receive a final transcript, and stop() would leave the socket
  // open waiting to deliver one whose callbacks would set state on an
  // unmounted component.
  useEffect(() => {
    return () => {
      sessionRef.current?.detach();
      sessionRef.current = null;
    };
  }, []);

  return { isStreaming, interimText, error, start, stop };
}

export default useSpeechStreaming;
